/**
 * The Result page's "Advanced" tab: clusters, homophily, the follow network and the community's
 * validation figures - as in the prototype, and with its warning that graphs this small are
 * unstable, so these are descriptive rather than findings.
 *
 * It was a collapsed disclosure until the page grew tabs (decision 8.15). The tab is the disclosure
 * now, so this draws its content and nothing else.
 */

import { useMemo } from "react";

import { Chart } from "@/charts/Chart";
import { followNetwork } from "@/charts/options";
import { TableView } from "@/charts/TableView";
import type { Dashboard } from "@/lib/queries";

export function Advanced({ view }: { view: Dashboard }) {
  const network = useMemo(() => (view.emergence ? followNetwork(view.emergence) : null), [view.emergence]);

  return (
    <section>
      <div className="space-y-6 rounded-xl border border-white/10 bg-[#16181d] p-4">
        {!view.emergence ? (
          <p className="text-sm text-muted-foreground">No network analysis for this run.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Figure label="Agreement modularity Q" value={format(view.emergence.modularity)} />
              <Figure
                label="Trait homophily ρ"
                value={format(view.emergence.homophily_rho, true)}
                note={view.emergence.homophily_p === null ? undefined : `QAP p = ${view.emergence.homophily_p}`}
              />
              <Figure
                label="Follows added / removed"
                value={`${view.emergence.follows_added} / ${view.emergence.follows_removed}`}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Homophily ρ is the Spearman correlation between trait similarity and net agreement among pairs who saw
              each other's posts. The QAP p-value comes from permuting persona labels. Graphs this small are
              unstable, so read these as descriptive.
            </p>

            <div>
              <h3 className="mb-2 text-sm font-medium">Final follow network</h3>
              <p className="mb-1 text-xs text-muted-foreground">
                Colour is the persona's final stance. Drag a persona to move it, scroll to zoom.
              </p>
              {network && (
                <Chart
                  option={network}
                  height={420}
                  description={`Follow network of ${view.emergence.nodes.length} personas with ${view.emergence.edges.length} follows, coloured by final stance`}
                />
              )}
              <TableView
                title="Network"
                rows={view.emergence.nodes.map((node) => ({
                  Persona: `${node.id} · ${node.name}`,
                  Stance: node.stance_label,
                  Cluster: node.cluster ?? "none",
                  Follows: view.emergence!.edges.filter((e) => e.source === node.id).length,
                  "Followed by": view.emergence!.edges.filter((e) => e.target === node.id).length,
                }))}
              />
            </div>

            {view.emergence.clusters.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-medium">Agreement clusters</h3>
                <TableView
                  title="Clusters"
                  rows={view.emergence.clusters.map((cluster) => ({
                    Cluster: cluster.cluster,
                    Size: cluster.size,
                    ...Object.fromEntries(
                      Object.entries(cluster.mean_traits).map(([trait, value]) => [
                        trait.slice(0, 1).toUpperCase(),
                        round(value),
                      ]),
                    ),
                    "Mean final stance": round(cluster.mean_final_stance),
                    Members: cluster.members.join(", "),
                  }))}
                />
              </div>
            )}
          </>
        )}

        <div>
          <h3 className="mb-2 text-sm font-medium">Validation (community)</h3>
          <TableView
            title="Validation"
            rows={view.validation.map((row) => ({
              Persona: `${row.persona_id} · ${row.name}`,
              Consistency: round(row.internal_consistency),
              "Retest r": round(row.test_retest_r),
              "Fidelity r": round(row.trait_fidelity_r),
              Flagged: row.flagged ? "yes" : "",
              Reasons: row.reasons.join("; "),
            }))}
          />
        </div>
      </div>
    </section>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.04] p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
    </div>
  );
}

function format(value: number | null, signed = false): string {
  if (value === null) return "–";
  return signed && value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
}

function round(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Math.round(value * 1000) / 1000;
}
